import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, Spinner } from './shared';

const CONTENT_TYPE_LABEL = {
    markdown: 'Markdown',
    json: 'JSON',
    html: 'HTML',
    plain: 'Plain text',
};

function StatCard({ label, value, tone = 'default' }) {
    const valueClass = {
        default: 'text-gray-800',
        warning: 'text-amber-700',
        success: 'text-emerald-700',
    }[tone] || 'text-gray-800';
    return (
        <div className="border border-gray-200 rounded-lg bg-white p-3">
            <div className="text-[11px] text-gray-500 uppercase">{label}</div>
            <div className={`text-sm font-medium mt-1 ${valueClass}`}>{value}</div>
        </div>
    );
}

function renderContent(content, contentType) {
    if (!content) return <div className="text-gray-400 text-sm">(empty)</div>;
    if (contentType === 'markdown') {
        return (
            <div className="prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{content}</ReactMarkdown>
            </div>
        );
    }
    if (contentType === 'json') {
        let pretty = content;
        try { pretty = JSON.stringify(JSON.parse(content), null, 2); } catch { /* fall through */ }
        return <pre className="text-xs font-mono text-gray-700 overflow-x-auto">{pretty}</pre>;
    }
    return <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">{content}</pre>;
}

export default function AdminInstrumentProfileDetail() {
    const { canonicalId } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('content');

    useEffect(() => { load(); }, [canonicalId]);

    const load = async () => {
        try {
            setLoading(true);
            const res = await adminApi.getInstrumentProfile(canonicalId);
            setProfile(res);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load profile');
            setProfile(null);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading profile..." center />;
    if (error && !profile) return (
        <div>
            <Button variant="ghost" size="xs" onClick={() => navigate('/admin/instrument-profiles')}>← Back</Button>
            <Alert variant="error" className="mt-4">{error}</Alert>
        </div>
    );
    if (!profile) return <Alert variant="warning">Profile not found</Alert>;

    const tags = profile.attributes?.tags || profile.tags || [];
    const sectors = profile.attributes?.sectors || profile.sectors || [];
    const watchlistPriority = profile.attributes?.watchlist_priority ?? profile.watchlist_priority;
    const indexedRev = profile.indexed_rev;
    const ragStale = indexedRev == null || indexedRev < profile.rev;
    const updatedAt = profile.updated_at ? new Date(profile.updated_at).toLocaleString() : '—';

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-6">
                <div>
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => navigate('/admin/instrument-profiles')}
                    >
                        ← Back to Profiles
                    </Button>
                    <h1 className="text-lg font-semibold text-gray-900 mt-1 font-mono break-all">
                        {profile.instrument_id || canonicalId}
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        <Badge variant="outline">{profile.asset_class}</Badge>
                        <span className="mx-2">·</span>
                        {profile.symbol}
                        {profile.venue && <><span className="mx-2">·</span>{profile.venue}</>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
                    <Button
                        size="sm"
                        onClick={() => navigate(`/admin/instrument-profiles/${encodeURIComponent(canonicalId)}/edit`)}
                    >
                        Edit
                    </Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <div className="grid grid-cols-4 gap-3 mb-6">
                <StatCard label="Rev" value={`v${profile.rev}`} />
                <StatCard
                    label="RAG indexed rev"
                    value={indexedRev == null ? '(not indexed)' : `v${indexedRev}`}
                    tone={ragStale ? 'warning' : 'success'}
                />
                <StatCard
                    label="Content type"
                    value={CONTENT_TYPE_LABEL[profile.content_type] || profile.content_type}
                />
                <StatCard
                    label="Updated"
                    value={<span className="text-xs font-mono">{updatedAt}</span>}
                />
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Tags</span>
                {tags.length === 0 ? (
                    <span className="text-xs text-gray-400">—</span>
                ) : tags.map((t) => <Badge key={t} variant="info">#{t}</Badge>)}

                <span className="mx-2 text-gray-300">|</span>

                <span className="text-xs text-gray-500 uppercase tracking-wide">Sectors</span>
                {sectors.length === 0 ? (
                    <span className="text-xs text-gray-400">—</span>
                ) : sectors.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}

                {watchlistPriority != null && (
                    <>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Priority</span>
                        <Badge variant="default">{watchlistPriority}</Badge>
                    </>
                )}

                {profile.updated_by && (
                    <>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-xs text-gray-500 uppercase tracking-wide">By</span>
                        <span className="text-xs font-mono text-gray-700">{profile.updated_by}</span>
                    </>
                )}
            </div>

            <div className="border-b border-gray-200 mb-3">
                <nav className="flex gap-4">
                    {['content', 'attributes'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {tab[0].toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="border border-gray-200 rounded-lg bg-white p-5">
                {activeTab === 'content' && renderContent(profile.content, profile.content_type)}
                {activeTab === 'attributes' && (
                    <pre className="text-xs font-mono text-gray-700 overflow-x-auto">
                        {JSON.stringify(profile.attributes || {}, null, 2)}
                    </pre>
                )}
            </div>
        </div>
    );
}
